import {
  PendingActive,
  PendingApproval,
  PendingInactive,
} from '../../constants';

export const getSortColumns = (params, defaultValue) => {
  let groupId = getGroupByColumns(params);
  let cols =
    params?.sortModel ??
    (typeof params?.sortController?.getSortModel === 'function'
      ? params.sortController.getSortModel()
      : params?.api
          ?.getColumnState?.()
          ?.filter((c) => c.sort)
          ?.sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
          ?.map((c) => ({ colId: c.colId, sort: c.sort }))) ??
    [];
  if (cols && cols.length > 0) {
    // on API param Add sort column name when groupby done with same column name else make it empty
    if (groupId === cols[0].colId || !groupId) {
      let groupSort = columnKeyReplacer(cols[0].colId) + ':' + cols[0].sort;
      // Adding extra sort to avoid duplicate data on pagination
      let defaultSort = defaultValue && !groupId ? ',' + defaultValue : '';
      return groupSort + defaultSort;
    } else {
      return '';
    }
  }
  return params.rowGroupCols && params.rowGroupCols.length > 0
    ? ''
    : defaultValue;
};

export const getFilterColumns = (params, dateKey, toIncludeDate) => {
  let filterCols = params.filterModel;
  let filterText = '';
  let isGroupBy =
    params?.rowGroupCols?.length > 0 && params?.groupKeys?.length > 0;
  if (isGroupBy) {
    params.rowGroupCols.forEach((grpCols, index) => {
      if (params.groupKeys[index] && dateKey !== grpCols && !toIncludeDate) {
        if (index !== 0) filterText = filterText + ' AND ';

        filterText =
          filterText +
          grpCols.id +
          filterMapper('equals', params.groupKeys[index]);
      }
    });
  }
  if (filterCols) {
    for (let col in filterCols) {
      if (
        !dateKey || // When only one parameter, filter into one string
        (dateKey === col && toIncludeDate) || // When 2nd parameter is passed to include date parameter
        (dateKey !== col && !toIncludeDate) // When 2nd parameter is passed to exclude date parameter
      ) {
        if (filterText.length > 0) {
          filterText = filterText + ' AND ';
        }
        filterText =
          filterText +
          columnKeyReplacer(col) +
          filterMapper(filterCols[col].type, filterCols[col].filter);
      }
    }
  }
  return filterText;
};

const columnKeyReplacer = (key) => {
  switch (key) {
    case 'distributor':
      return 'deliveryPlatform';
    case 'contractNumber_1':
      return 'contractNumber';
    case 'modifiedDate_1':
      return 'modifiedDate';
    default:
      return key;
  }
};

const filterMapper = (key, value) => {
  // Using it for user dashboard redirection props to user entitlement page
  // as we are hiding inRange option on UI
  if (
    key === 'contains' &&
    (value === `"${PendingActive}","${PendingInactive}"` ||
      value === `"${PendingApproval}"`)
  )
    key = 'inRange';

  switch (key) {
    case 'equals':
      return ':"' + value + '"';
    case 'notEqual':
      return '!:"' + value + '"';
    case 'contains':
      return ':"*' + value + '*"';
    case 'notContains':
      return '!:"*' + value + '*"';
    case 'startsWith':
      return ':"' + value + '*"';
    case 'endsWith':
      return ':"*' + value + '"';
    case 'inRange':
      return ' IN(' + value + ')';
    case 'dateBetween':
      return value;
    default:
      return '';
  }
};

export const getGroupByColumns = (params) => {
  let cols = params.rowGroupCols;
  if (cols && cols.length > 0) {
    for (let i = 0; i < cols.length; i++) {
      if (!params.groupKeys[i]) return cols[i].id;
    }
  }
  return '';
};

export const isGroupBy = (params) => {
  // returns a boolean if grid is using groupby or not
  let isGroupBy = false;
  if (
    params?.rowGroupCols?.length > 0 &&
    params?.rowGroupCols?.length !== params?.groupKeys?.length
  ) {
    isGroupBy = true;
  }
  return isGroupBy;
};

export const filterOptions = [
  'contains',
  'notContains',
  'equals',
  'notEqual',
  'startsWith',
  'endsWith',
];

export const resizeColumnAsPerValue = (params, rowData) => {
  try {
    if (!params || !rowData?.length) return;
    // AG Grid v31: columnApi is merged into api
    const columnApi = params.columnApi || params.api;
    if (!columnApi) return;

    // Scope the query to this specific grid to avoid measuring a sibling
    // grid's viewport when multiple AG Grid instances are on the same page.
    const gridId = params.api?.getGridId?.();
    const gridRoot = gridId
      ? document.querySelector(`[grid-id="${gridId}"]`)
      : null;
    const gridDiv = gridRoot
      ? gridRoot.querySelector('.ag-header-viewport')
      : document.querySelector('.ag-header-viewport');
    if (!gridDiv) return;
    const gridWidth = gridDiv.offsetWidth - 10;

    const defaultMinWidth = 51;
    const defaultMaxWidth = 400;
    const charWidth = 6;
    const charWidthHeader = 8;
    const buffer = 17 * 2 + 10;
    const bufferGrp = 17 * 2 + 17 + 50;
    const headerBuffer = 18 * 2 + 20;

    const allColumns = columnApi.getAllDisplayedColumns();
    const colWidths = [];

    allColumns.forEach((col) => {
      const colDef = col.getColDef();
      const { field, headerName = '', showRowGroup } = colDef;

      let finalWidth;

      if (!field && showRowGroup) {
        const groupField = showRowGroup;
        const header = headerName || groupField;
        let maxValue = '';
        rowData.forEach((row) => {
          const val = String(row[groupField] ?? '').trim();
          if (val.length > maxValue.length) maxValue = val;
        });
        const groupContentWidth = maxValue.length * charWidth + bufferGrp;
        const headerWidth = header.length * charWidthHeader + headerBuffer;
        finalWidth = Math.min(
          Math.max(groupContentWidth, headerWidth, defaultMinWidth),
          defaultMaxWidth,
        );
      } else if (!field) {
        finalWidth = defaultMinWidth;
      } else {
        const header = headerName || field;
        const headerWidth = header.length * charWidthHeader + headerBuffer;
        const maxWidth = colDef.maxWidth || defaultMaxWidth;
        const contentWidth = rowData.reduce((max, row) => {
          const val = String(row[field] ?? '').trim();
          return Math.max(max, val.length * charWidth + buffer);
        }, 0);
        finalWidth = Math.min(
          Math.max(headerWidth, contentWidth, defaultMinWidth),
          maxWidth,
        );
      }

      colWidths.push({ col, width: finalWidth });
      columnApi.setColumnWidth(col, finalWidth, false);
    });

    const totalWidth = colWidths.reduce((sum, item) => sum + item.width, 0);
    if (gridWidth > 0 && totalWidth < gridWidth) {
      // Delegate to AG Grid's own fill mechanism. It uses its internal
      // viewport measurement (accounting for the vertical scrollbar and
      // minWidth/maxWidth constraints) and proportionally scales the
      // content-based widths we just set above to eliminate blank space.
      params.api.sizeColumnsToFit();
    }
  } catch (error) {}
};

export const onExportClientSide = (
  gridRef,
  fileName = 'ExportData',
  sheetName,
) => {
  try {
    if (!gridRef.current) return;
    // Get all row data once
    const allData = [];
    gridRef.current.api.forEachNode((node) => {
      allData.push(node.data);
    });

    // Define columnWidth callback using the above cached data
    const columnWidthCallback = (params) => {
      const column = params.column;
      const field = column?.getColDef().field;
      const colDef = params.column.getColDef();

      if (!field) return 25;

      let maxLength = field.length;

      for (const row of allData) {
        let value;

        // Check for valueGetter for custom text
        if (colDef.valueGetter) {
          value = colDef.valueGetter({
            data: row,
            node: { data: row },
            colDef: colDef,
            api: gridRef.current.api,
            columnApi: gridRef.current.columnApi,
            context: gridRef.current.context,
          });
        } else {
          value = row[field];
        }

        const str = value != null ? String(value) : '';
        if (str.length > maxLength) {
          maxLength = str.length;
        }
      }

      return Math.min(Math.max(maxLength * 6 + 11, 25), 300); // 6 is the per character width & 11 is marginal width
    };

    // Export with custom width
    gridRef.current.api.exportDataAsExcel({
      columnWidth: columnWidthCallback,
      fileName: fileName,
      sheetName: sheetName ?? fileName,
    });
  } catch (err) {}
};
